import * as React from "react";
import { connect } from "react-redux";

import { setToModel } from "../../../Engine/ActionCreators/AllActionCreators";
import {
    highlightComponents,
    unhighlightComponents,
} from "../../../Engine/ActionCreators/HighlightComponentActionCreators";
import { CandyDispatch } from "../../../Engine/Core/Store/CandyReduxTypes";
import { FullFormState } from "../../../Engine/Core/Store/FullFormState";
import { ViewModelSelect } from "../../../Helpers/StateAccessors/ViewModelSelect";
import { withControlDebugInfo } from "../../withControlDebugInfo";
import { EvaluableProp, withEvaluators } from "../../withEvaluators";
import { withMetrics } from "../../withMetrics";
import { withResolvePaths } from "../../withResolvePaths";
import { withViewModelSelect } from "../../withViewModelSelect";
import { withVisibilityProps } from "../../withVisibilityProps";
import { withExternalMountingEventReporting } from "../../Services/ExternalMountingEventReporting/withExternalMountingEventReporting";
import { withFocusManagement } from "../../Services/FocusManagement/withFocusManagement";
import { AbsoluteResolvedModelPath } from "../../../../../Common/ModelPath/ModelPath";
import { withFocusOnNextValueEditor } from "../../../Helpers/JumpByEnterOnNextControl/WithFocusOnNextValueEditor";
import { checkIsSuitableToClick, externalOnClickHandler } from "../../../Engine/ActionCreators/ExternalOnClickAction";
import { withTestPageObjectRegistration } from "../../Services/TestPageObjects/PageObjectExposure";
import { WithHighlightedInjectableProps, withHighlighting } from "../../withHighlighting";

import { FxInput } from "./FxInput";

interface FxInputContainerProps extends WithHighlightedInjectableProps {
    bindingPath: AbsoluteResolvedModelPath;
    viewModelSelect: ViewModelSelect;
    formula?: EvaluableProp<React.ReactNode>;
    processEvaluableProp: <T>(state: FullFormState, prop: EvaluableProp<T>) => T;
    visible?: EvaluableProp<boolean>;
    disabled?: boolean;
    disabled2?: boolean;
    disabledPure?: boolean;
    isReadOnly?: boolean;
}

const mapStateToProps = (state: FullFormState, ownProps: FxInputContainerProps) => {
    const { bindingPath, viewModelSelect, disabled, disabledPure, processEvaluableProp, disabled2 } = ownProps;
    const controlModel = viewModelSelect.controlModel(state, bindingPath);
    const autoFlag = controlModel.autoFlag ?? true;

    return {
        autoFlag: autoFlag,
        autoValue: controlModel.autoValue,
        ignoreAutoFlag: controlModel.ignoreAutoFlag,
        disabledPure: ownProps.disabled || ownProps.disabledPure,
        disabled: processEvaluableProp(state, disabled2) || disabledPure || (disabled ?? controlModel.disabled),
        value: autoFlag ? controlModel.autoValue : controlModel.value,
        error: controlModel.error,
        warning: controlModel.warning,
        isTooltipEnabled: controlModel.isTooltipEnabled,
        formula: ownProps.processEvaluableProp(state, ownProps.formula),
        highlighted: ownProps.highlighted,
        visible: ownProps.processEvaluableProp(state, ownProps.visible),
        isReadOnly: state.settings.isReadOnly,
        isLink: checkIsSuitableToClick(state.settings, bindingPath),
        highlightingTooltipText: ownProps.highlightingTooltipText,
        isTooltipFixed: ownProps.isTooltipFixed,
        onTooltipClose: ownProps.onTooltipClose,
    };
};

const mapDispatchToProps = (dispatch: CandyDispatch, ownProps: FxInputContainerProps) => {
    const { bindingPath } = ownProps;

    return {
        setIgnoreAutoFlag: (value: boolean) => dispatch(setToModel(bindingPath, "ignoreAutoFlag", value)),
        setValue: (value: undefined | string) => dispatch(setToModel(bindingPath, "value", value)),
        highlightComponents: () => dispatch(highlightComponents(bindingPath)),
        unhighlightComponents: () => dispatch(unhighlightComponents()),
        onClick: () => externalOnClickHandler(dispatch, bindingPath),
    };
};

export const FxInputContainer = withMetrics(
    withFocusOnNextValueEditor(
        withResolvePaths(
            withViewModelSelect(
                withEvaluators(
                    withHighlighting(
                        connect(
                            mapStateToProps,
                            mapDispatchToProps
                        )(
                            withVisibilityProps(
                                withTestPageObjectRegistration(
                                    withFocusManagement(
                                        withExternalMountingEventReporting(withControlDebugInfo(FxInput))
                                    )
                                )
                            )
                        )
                    )
                )
            )
        )
    )
);
